<template>
  <div
    ref="panelRef"
    class="work-orders-map-panel"
  >
    <map-base
      ref="mapBaseRef"
      :show-geo-search-control="false"
      class="work-orders-map-panel__map"
      @markers:added="onMarkersAdded"
    />

    <!-- Opened by clicking a photograph in a report's popup. Preview and download only:
         the pictures come off the driver's device and are not ours to remove. -->
    <images-preview-dialog
      v-if="imagesPreview.isOpen"
      :show-dialog="imagesPreview.isOpen"
      :images="imagesPreview.images"
      :initial-image-index="imagesPreview.initialIndex"
      :dialog-title="imagesPreview.title"
      :enable-delete="false"
      @close="closeImagesPreview"
    />
  </div>
</template>

<script setup>
import L from 'leaflet'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import MapBase from '@/global/components/map/MapBase.vue'
import ImagesPreviewDialog from '@/global/components/dialogs/ImagesPreviewDialog.vue'
import { useImagesPreview } from '@/modules/road-maintenance-module/winter-service-v2/common/composables/useImagesPreview'
import { isArray } from '@/global/utils/utils'
import { useTranslate } from '@/global/composables/useTranslate'
import {
  buildStreetShapes,
  COVERAGE_COLOR,
  COVERAGE_STATE
} from '@/modules/road-maintenance-module/winter-service-v2/tasks/composables/useStreetShapes'
import {
  escapeHtml,
  useIrregularityMarkers
} from '@/modules/road-maintenance-module/winter-service-v2/irregularities/composables/useIrregularityMarkers'
import { WORK_ORDERS_OVERVIEW_TRANSLATION_NAMESPACE as NS } from '@/modules/road-maintenance-module/winter-service-v2/work-orders/overview/config/constants'

/**
 * What the tracker says a vehicle is doing, and what this map calls it. The backend
 * hands the raw state over - anything not listed here is simply not shown, which is what
 * a vehicle that has never reported comes back as.
 */
const VEHICLE_STATUS_LABEL_KEYS = {
  DRIVING: 'vehicle_status_driving',
  IDLING: 'vehicle_status_idling',
  CONTACT: 'vehicle_status_contact',
  MOVING: 'vehicle_status_moving',
  PARKING: 'vehicle_status_parking',
  PRIVATE: 'vehicle_status_private',
  NO_GPS: 'vehicle_status_no_gps',
  OFFLINE: 'vehicle_status_offline'
}

const VEHICLE_MARKER_SIZE = 40
const VEHICLE_ICON_KEY_PREFIX = 'wsWorkOrderVehicle'
const VEHICLE_MARKER_ID_PREFIX = 'ws-work-order-vehicle-'
// Clear of the disc, so the tooltip never sits under the cursor that opened it.
const VEHICLE_TOOLTIP_OFFSET = [0, -(VEHICLE_MARKER_SIZE / 2) - 2]

/**
 * What the marker is painted in, by what the vehicle is doing. The colours are the ones
 * the rest of the screen already means these things by - the coverage green for a vehicle
 * that is working, red for one there is something wrong with - so a map full of vehicles
 * can be read before a single one is hovered.
 */
const VEHICLE_STATUS_COLOR = {
  DRIVING: '#65AD45',
  MOVING: '#65AD45',
  CONTACT: '#F59E0B',
  IDLING: '#F59E0B',
  PARKING: '#6B7280',
  PRIVATE: '#6B7280',
  // Not a state the vehicle is in so much as one the marker is in: the position is the
  // last one reported and may be an hour old.
  OFFLINE: '#EF4444',
  NO_GPS: '#EF4444'
}

const VEHICLE_DEFAULT_COLOR = '#6B7280'

/**
 * The vehicle as a sign on the map: a white disc ringed in the colour of what it is
 * doing, the car drawn inside it in the same colour, and a pointer on the rim for the way
 * it is facing.
 *
 * White rather than the reports' dark disc on purpose - the two have to be told apart at
 * a glance, and on a map of green and red route stretches a light marker with a coloured
 * ring stays legible where another black circle would just be a second kind of dot.
 *
 * The pointer is `course` as the tracker reports it: degrees clockwise from north, which
 * is exactly how an svg rotation reads with 0 pointing up. The car itself is left upright
 * - it is drawn from the side, and a side view turned on its head is not a vehicle
 * heading south, it is an upside-down car.
 */
function buildVehicleIcon (vehicle) {
  const color = VEHICLE_STATUS_COLOR[vehicle?.status] || VEHICLE_DEFAULT_COLOR
  const heading = Number(vehicle?.course)
  const pointer = Number.isFinite(heading)
    ? `
      <g transform="rotate(${heading} 20 20)">
        <path d="M20 1.5 L24.4 8.4 L15.6 8.4 Z" fill="${color}"/>
      </g>
    `
    : ''

  return L.divIcon({
    className: 'ws-work-order-vehicle-marker',
    iconSize: [VEHICLE_MARKER_SIZE, VEHICLE_MARKER_SIZE],
    iconAnchor: [VEHICLE_MARKER_SIZE / 2, VEHICLE_MARKER_SIZE / 2],
    html: `
      <svg width="${VEHICLE_MARKER_SIZE}" height="${VEHICLE_MARKER_SIZE}" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
        ${pointer}
        <circle cx="20" cy="20" r="12" fill="#FFFFFF" stroke="${color}" stroke-width="2.5"/>
        <g transform="translate(12.9 13.6) scale(0.85)">
          <path
            d="M1.85185 5.625L3.24074 1.40625H13.4259L14.8148 5.625M13.4259 10.3125C13.0576 10.3125 12.7043 10.1643 12.4438 9.90062C12.1834 9.6369 12.037 9.27921 12.037 8.90625C12.037 8.53329 12.1834 8.1756 12.4438 7.91188C12.7043 7.64816 13.0576 7.5 13.4259 7.5C13.7943 7.5 14.1476 7.64816 14.408 7.91188C14.6685 8.1756 14.8148 8.53329 14.8148 8.90625C14.8148 9.27921 14.6685 9.6369 14.408 9.90062C14.1476 10.1643 13.7943 10.3125 13.4259 10.3125ZM3.24074 10.3125C2.87238 10.3125 2.51911 10.1643 2.25865 9.90062C1.99818 9.6369 1.85185 9.27921 1.85185 8.90625C1.85185 8.53329 1.99818 8.1756 2.25865 7.91188C2.51911 7.64816 2.87238 7.5 3.24074 7.5C3.6091 7.5 3.96237 7.64816 4.22283 7.91188C4.4833 8.1756 4.62963 8.53329 4.62963 8.90625C4.62963 9.27921 4.4833 9.6369 4.22283 9.90062C3.96237 10.1643 3.6091 10.3125 3.24074 10.3125ZM14.7407 0.9375C14.5556 0.39375 14.037 0 13.4259 0H3.24074C2.62963 0 2.11111 0.39375 1.92593 0.9375L0 6.5625V14.0625C0 14.3111 0.0975526 14.5496 0.271197 14.7254C0.444842 14.9012 0.680355 15 0.925926 15H1.85185C2.09742 15 2.33294 14.9012 2.50658 14.7254C2.68022 14.5496 2.77778 14.3111 2.77778 14.0625V13.125H13.8889V14.0625C13.8889 14.3111 13.9864 14.5496 14.1601 14.7254C14.3337 14.9012 14.5692 15 14.8148 15H15.7407C15.9863 15 16.2218 14.9012 16.3955 14.7254C16.5691 14.5496 16.6667 14.3111 16.6667 14.0625V6.5625L14.7407 0.9375Z"
            fill="${color}"
          />
        </g>
      </svg>
    `
  })
}

const props = defineProps({
  // The routes to draw, as the task streets endpoint reports them. Whoever owns the
  // selection decides what is worth looking at - this only paints it.
  streets: {
    type: Array,
    default: () => ([])
  },
  // The reports to mark, as the irregularities listing reports them.
  irregularities: {
    type: Array,
    default: () => ([])
  },
  // Where the marked orders' vehicles are standing right now, as the positions endpoint
  // reports them - see `useWorkOrderVehiclePositions`. Left empty the map simply draws
  // none, which is what the details screens pass.
  vehicles: {
    type: Array,
    default: () => ([])
  },
  // DataTable hands a side-table component its `config`; the table composable puts
  // both lists on it. Read as a fallback so the same panel serves the listing's side
  // map and the details screens, which pass the lists directly.
  config: {
    type: Object,
    default: () => ({})
  }
})

const { translate } = useTranslate()

const {
  state: imagesPreview,
  open: openImagesPreview,
  close: closeImagesPreview
} = useImagesPreview()

// The reports are drawn by the same code the irregularities screen draws them with -
// they are the same reports, and a sign here has to mean what a sign there means. Only
// the popup's row labels are this screen's own: a work order's map says "Vrijeme" and
// "Ulica" where the listing says "Datum prijave" and "Adresa".
const { renderMarkers } = useIrregularityMarkers({
  translationNamespace: NS,
  onPhotoPreview: openImagesPreview
})

const mapBaseRef = ref(null)
const panelRef = ref(null)
// What each vehicle marker says on hover, by marker id - filled while the markers are
// built and read back once MapBase has created their layers. See `onMarkersAdded`.
const vehicleTooltips = new Map()
let isMapReady = false
let resizeObserver = null

const streets = computed(() => {
  if (isArray(props.streets) && props.streets.length) {
    return props.streets
  }

  return isArray(props.config?.streets) ? props.config.streets : []
})

const irregularities = computed(() => {
  if (isArray(props.irregularities) && props.irregularities.length) {
    return props.irregularities
  }

  return isArray(props.config?.irregularities) ? props.config.irregularities : []
})

const vehicles = computed(() => {
  if (isArray(props.vehicles) && props.vehicles.length) {
    return props.vehicles
  }

  return isArray(props.config?.vehicles) ? props.config.vehicles : []
})

function popupRow (label, value) {
  if (value === null || value === undefined || value === '') {
    return ''
  }

  return `
    <div class="work-order-popup__row">
      <span class="work-order-popup__label">${escapeHtml(label)}</span>
      <span class="work-order-popup__value">${escapeHtml(value)}</span>
    </div>
  `
}

/**
 * What one stretch of a street says when the cursor is on it.
 *
 * A coverage line answers three questions a dispatcher would otherwise have to open the
 * order for: how this piece went, how many passes it has had of the number it needs, and
 * how much of the street that is. The street's own figure is there too, so a stretch can
 * be read without losing sight of the whole.
 *
 * The headline reads "Izvršeno - 45 % (Djelomično očišćeno)": the figure is what is being
 * asked, and what the figure amounts to is said after it, in the colour the stretch is
 * drawn in. Colouring the whole line the way it used to be made the percentage itself
 * read as a warning; only the state is a state, so only the state carries the colour.
 */
function buildCoverageTooltip (street, segment) {
  const { covered, partial } = COVERAGE_STATE
  const stateKey = {
    [covered]: 'coverage_covered',
    [partial]: 'coverage_partial'
  }[segment?.state] || 'coverage_uncovered'

  const stateColor = COVERAGE_COLOR[segment?.state] || COVERAGE_COLOR[COVERAGE_STATE.uncovered]
  const percentLabel = toPercentLabel(segment?.percent)

  const rows = [
    `<div class="street-tooltip__title">${escapeHtml(streetTitle(street))}</div>`,
    `<div class="street-tooltip__state">
      ${escapeHtml(translate(`${NS}.coverage_done`))}${percentLabel ? ` - ${escapeHtml(percentLabel)}` : ''}
      <span class="street-tooltip__state-name" style="color: ${stateColor}">(${escapeHtml(translate(`${NS}.${stateKey}`))})</span>
    </div>`,
    popupRow(translate(`${NS}.passes_forward`), toPassesLabel(segment?.passes?.forward)),
    popupRow(translate(`${NS}.passes_backward`), toPassesLabel(segment?.passes?.backward)),
    popupRow(translate(`${NS}.section_length`), toMetresLabel(segment?.length)),
    popupRow(translate(`${NS}.direction_mode`), toDirectionLabel(street?.coverage?.direction_mode)),
    popupRow(translate(`${NS}.street_total`), toPercentLabel(street?.coverage?.percent))
  ].join('')

  return `<div class="work-order-popup street-tooltip">${rows}</div>`
}

function streetTitle (street) {
  return [street?.street_name, street?.street_district].filter(Boolean).join(' - ')
}

function toPercentLabel (percent) {
  return percent === null || percent === undefined ? null : `${Number(percent)} %`
}

function toMetresLabel (metres) {
  return metres === null || metres === undefined ? null : `${Math.round(Number(metres))} m`
}

/**
 * "1 / 2" for a direction that asks for passes. A direction that asks for none is left
 * out of the tooltip entirely rather than shown as 0/0 - a one-way street has nothing to
 * say about the way it is not driven.
 */
function toPassesLabel (passes) {
  const required = Number(passes?.required) || 0

  return required < 1 ? null : `${Number(passes?.done) || 0} / ${required}`
}

function toDirectionLabel (directionMode) {
  if (!directionMode) {
    return null
  }

  return translate(`${NS}.${directionMode === 'one_way' ? 'direction_one_way' : 'direction_two_way'}`)
}

function renderStreets () {
  mapBaseRef.value.removeAllPolylines()

  const { polylines, circles } = buildStreetShapes(streets.value, {
    tooltipClass: 'work-orders-map-tooltip',
    coverageTooltip: buildCoverageTooltip
  })

  if (polylines.length || circles.length) {
    mapBaseRef.value.generatePolylines({ polylines, circles })
  }
}

function renderIrregularities () {
  // The routes are what the view is fitted to where there are any.
  renderMarkers(mapBaseRef.value, irregularities.value, {
    fitMarkers: !streets.value.length
  })
}

/**
 * What a vehicle says when the cursor is on it.
 *
 * The registration names it, and the rest is what a dispatcher would otherwise open the
 * order for: who is driving, what the vehicle is doing, how fast it is going, and which
 * order it is marked for, since several may be marked at once.
 */
function buildVehicleTooltip (vehicle) {
  const rows = [
    `<div class="street-tooltip__title">${escapeHtml(vehicle?.registration || '')}</div>`,
    popupRow(translate(`${NS}.driver`), vehicle?.driver_name),
    popupRow(translate(`${NS}.vehicle_status`), toVehicleStatusLabel(vehicle?.status)),
    popupRow(translate(`${NS}.speed`), toSpeedLabel(vehicle?.speed)),
    popupRow(translate(`${NS}.work_order`), vehicle?.order_id)
  ].join('')

  return `<div class="work-order-popup street-tooltip">${rows}</div>`
}

/**
 * Driving, parked, out of contact. It is also how fresh the marker is: a tracker that has
 * stopped reporting is OFFLINE within twenty minutes and one without a fix is NO_GPS, so
 * a vehicle still standing where it last reported says so itself.
 */
function toVehicleStatusLabel (status) {
  const key = VEHICLE_STATUS_LABEL_KEYS[status]

  return key ? translate(`${NS}.${key}`) : null
}

function toSpeedLabel (speed) {
  return speed === null || speed === undefined ? null : `${Math.round(Number(speed))} km/h`
}

/**
 * The marked orders' vehicles, where they are standing now.
 *
 * Added to whatever the reports have already put on the map rather than replacing it -
 * `keepExisting` - and never fitted to unless they are the only thing drawn: the route is
 * what the view is about, and a vehicle that has wandered off it must not drag the whole
 * map after it.
 */
function renderVehicles () {
  vehicleTooltips.clear()

  // An icon per vehicle rather than one shared between them: each is turned to its own
  // heading, so no two are quite the same drawing.
  const icons = {}

  const markers = vehicles.value.reduce((collected, vehicle) => {
    const { latitude, longitude } = vehicle || {}

    if (latitude === null || latitude === undefined || longitude === null || longitude === undefined) {
      return collected
    }

    const iconKey = `${VEHICLE_ICON_KEY_PREFIX}-${vehicle.vehicle_id}`
    const markerId = `${VEHICLE_MARKER_ID_PREFIX}${vehicle.vehicle_id}`

    icons[iconKey] = buildVehicleIcon(vehicle)
    vehicleTooltips.set(markerId, buildVehicleTooltip(vehicle))

    collected.push({
      id: markerId,
      lat: Number(latitude),
      lon: Number(longitude),
      icon: iconKey,
      // Two vehicles parked at the same base must both be seen.
      alwaysVisible: true
    })

    return collected
  }, [])

  if (!markers.length) {
    return
  }

  mapBaseRef.value.generateMarkers({
    markers,
    icons,
    useClustering: false,
    keepExisting: true,
    fitMarkers: !streets.value.length && !irregularities.value.length
  })
}

/**
 * Gives every vehicle marker MapBase has just created its hover tooltip.
 *
 * A tooltip rather than the marker popup MapBase can open on hover: a popup is part of
 * the map, so it opens under the cursor that opened it and pans the map to fit itself -
 * the pointer then leaves the marker, the popup closes, and hovering a vehicle turned
 * into a flicker that walked the map away. A tooltip ignores the pointer entirely, which
 * is what hovering wants, and it is what the routes on this map already use.
 *
 * MapBase reports every marker it adds, the reports' signs among them, so only ours are
 * picked out of the batch.
 */
function onMarkersAdded ({ markers } = {}) {
  (markers || []).forEach(layer => {
    const content = vehicleTooltips.get(layer?.options?.id)

    if (!content) {
      return
    }

    layer.bindTooltip(content, {
      direction: 'top',
      offset: VEHICLE_TOOLTIP_OFFSET,
      className: 'work-orders-map-tooltip',
      opacity: 1
    })
  })
}

function render () {
  if (!isMapReady || !mapBaseRef.value) {
    return
  }

  renderStreets()
  // Before the vehicles: drawing the reports clears whatever markers were on the map.
  renderIrregularities()
  renderVehicles()
}

onMounted(() => {
  // Registering through `onMapReady` rather than the `ready` event skips MapBase's own
  // default company/Zagreb re-centering, which would otherwise run right after and
  // override the fitBounds the drawing does.
  mapBaseRef.value?.onMapReady(() => {
    isMapReady = true
    render()
  })

  // The panel is laid out against whatever sits next to it, so its height moves with
  // that. Leaflet only learns about a resized container when it is told, and until it
  // is it keeps painting tiles for the size it was first laid out at.
  if (typeof ResizeObserver === 'undefined' || !panelRef.value) {
    return
  }

  resizeObserver = new ResizeObserver(() => mapBaseRef.value?.invalidateSize?.())
  resizeObserver.observe(panelRef.value)
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  resizeObserver = null
})

watch(streets, render, { deep: true })
watch(irregularities, render, { deep: true })
watch(vehicles, render, { deep: true })
</script>

<script>
export default {
  name: 'WorkOrdersMapPanel',
  // Used as a side-table component too, and DataTable hands that slot a handful of
  // props this panel has no use for - they must not land as attributes on the root.
  inheritAttrs: false
}
</script>

<style scoped>
.work-orders-map-panel {
  display: flex;
  height: 100%;
  width: 100%;
  min-height: 320px;
  border-radius: 16px;
  overflow: hidden;
  /* Leaflet's own controls/panes reach z-index 1000-9999 internally. Without its own
     stacking context that competes with page-level chrome like the sticky app header
     once the page scrolls. */
  isolation: isolate;
}

.work-orders-map-panel__map {
  flex: 1 1 auto;
}
</style>

<style lang="scss">
/* Leaflet tooltips default to `white-space: nowrap`, so a long street name grew the
   tooltip wider than the panel itself and got clipped at its edge. */
.leaflet-tooltip.work-orders-map-tooltip {
  width: max-content;
  max-width: 240px;
  white-space: normal;
  overflow-wrap: break-word;
  text-align: center;
  /* Leaflet rounds a tooltip by 3px and a popup by 12px; the app's own boxes are 8px, and
     so are both of this map's tooltips - the route's and the vehicle's. */
  border-radius: 8px;
  padding: 8px 10px;
  box-shadow: 0 2px 8px rgba(17, 24, 39, 0.18);
}

/* Unscoped: the coverage tooltip is appended by Leaflet outside this component's scoped
   DOM tree, so a scoped rule would never reach it. The reports' own sign and popup are
   styled by `useIrregularityMarkers`, which is what draws them. */
.work-order-popup {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  color: #111827;
}

.work-order-popup__row {
  display: flex;
  gap: 6px;
  align-items: baseline;
}

.work-order-popup__label {
  color: #6B7280;
  white-space: nowrap;
}

/* The hover tooltip on a coverage stretch. It reuses the popup's rows, so only what it
   adds is styled here. A street name alone is centred well enough, but a list of labels
   and values is not - it reads down the left edge. */
.street-tooltip {
  min-width: 170px;
  text-align: left;
}

.street-tooltip__title {
  font-weight: 700;
  margin-bottom: 2px;
  overflow-wrap: anywhere;
}

.street-tooltip__state {
  font-weight: 700;
  margin-bottom: 4px;
}

.work-order-popup__value {
  font-weight: 700;
  overflow-wrap: anywhere;
}

/* The disc the car sits on carries the whole marker - leaflet's own marker styles would
   otherwise leave the inline svg with a background of its own. The shadow is what lifts a
   white marker off a pale map. */
.ws-work-order-vehicle-marker {
  background: transparent;
  border: none;
  filter: drop-shadow(0 2px 4px rgba(17, 24, 39, 0.35));
}
</style>
